import { PageRenderer } from "@/components/sections/PageRenderer";
import { SectionTitle } from "@/components/common/SectionTitle";
import { CoachBlock } from "@/components/cards/CoachBlock";
import { PageSections } from "@/components/common/PageSections";
import { CtaBand } from "@/components/common/CtaBand";
import { SplitWord } from "@/components/common/SplitWord";
import { Loader } from "@/components/common/Loader";
import { usePublicContent } from "@/lib/useContent";
import { Brain, Dumbbell, Heart, Target, Users, Building2, Smartphone, Volleyball, Award, Trophy, Sparkles, HeartHandshake } from "lucide-react";

const PILLARS = [
  { icon: Brain, title: "Mental", text: "Build discipline, focus, and the mindset of a champion — on and off the field." },
  { icon: Dumbbell, title: "Physical", text: "Develop real strength, speed, and athletic performance through proven programming." },
  { icon: Heart, title: "Spiritual", text: "Find balance, peace, and purpose. We train the whole person, not just the body." },
];

const AUDIENCES = [
  { icon: Trophy, title: "Athletes 8+", text: "Youth and competitive athletes building speed, strength, and confidence." },
  { icon: Heart, title: "Adults & Children", text: "Fitness for every age and level — families training together with purpose." },
  { icon: Building2, title: "Teams & Organizations", text: "Group and team training for schools, clubs, and local organizations." },
  { icon: Smartphone, title: "Online Clients", text: "Train anywhere with the Trilogy App, custom plans, and live coaching." },
  { icon: Volleyball, title: "Mavs Volleyball", text: "Club athletes and parents in our youth volleyball development program." },
];

const CREDIBILITY = [
  { value: "20+", label: "Years of coaching" },
  { value: "1000+", label: "Athletes trained" },
  { value: "2", label: "KC-metro locations" },
  { value: "Pro", label: "Former pro & elite trainers" },
];

const GALLERY = [
  "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=800&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&q=70&auto=format&fit=crop",
];

export default function About() { return <PageRenderer pageKey="about" />; }

export const AboutBody = () => {
  const { data: coaches, loading } = usePublicContent("coaches");
  return (
    <div>
      {/* hero via PageRenderer */}

      {/* Mission & Vision */}
      <section id="about-mission" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto grid w-full max-w-6xl gap-6 px-4 sm:px-6 md:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-8">
            <div className="flex items-center gap-3"><Target className="h-7 w-7 text-brand" /><h2 className="display-title text-3xl">Our Mission</h2></div>
            <p className="mt-4 text-muted-foreground leading-relaxed">To help every athlete and family achieve a life of fitness and a higher quality of health — training the whole person mentally, physically, and spiritually. We meet you where you are and build a clear path to where you want to be.</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-8">
            <div className="flex items-center gap-3"><Sparkles className="h-7 w-7 text-brand" /><h2 className="display-title text-3xl">Our Vision</h2></div>
            <p className="mt-4 text-muted-foreground leading-relaxed">To be the most trusted performance and fitness community in the Kansas City metro — developing confident athletes, healthier families, and stronger neighborhoods through purpose-driven training.</p>
          </div>
        </div>
      </section>

      {/* Training Philosophy */}
      <section id="about-philosophy" className="scroll-mt-24 bg-muted/30 py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle align="center" eyebrow="Our Philosophy" title={<SplitWord left="THE " right="TRILOGY" />} subtitle="Three pillars guide everything we do." />
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="rounded-xl border border-border bg-card p-7 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white"><Icon className="h-7 w-7" /></div>
                  <h3 className="mt-4 display-title text-3xl">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section id="about-serve" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Our Community" title={<SplitWord left="WHO WE " right="SERVE" />} subtitle="Trilogy is for everyone ready to grow — from first-time movers to elite competitors." />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              return (
                <div key={a.title} data-testid="audience-card" className="flex items-start gap-4 rounded-xl border border-border bg-card p-6">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-brand text-white"><Icon className="h-6 w-6" /></div>
                  <div>
                    <h3 className="display-title text-2xl">{a.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{a.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credibility */}
      <section id="about-experience" className="scroll-mt-24 relative overflow-hidden bg-black py-16 text-white grain">
        <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6">
          <SectionTitle align="center" eyebrow="Coaching Credibility" title={<span className="text-white"><SplitWord left="EXPERIENCE " right="YOU TRUST" /></span>} subtitle={<span className="text-white/70">Our coaches are former pro athletes, master elite personal trainers, and certified specialists.</span>} />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CREDIBILITY.map((c) => (
              <div key={c.label} className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <div className="display-title text-5xl text-brand">{c.value}</div>
                <div className="mt-2 text-sm uppercase tracking-wide text-white/70">{c.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-white/60"><Award className="h-4 w-4 text-brand" /> Motivational content from former Pro Athletes, Master Elite Personal Trainers, Herbalists & wellness pros.</div>
        </div>
      </section>

      {/* Community / Youth Development */}
      <section id="about-community" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-8 px-4 sm:px-6 md:grid-cols-2">
          <div className="overflow-hidden rounded-xl">
            <img src="https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1000&q=70&auto=format&fit=crop" alt="Youth development" loading="lazy" className="h-72 w-full object-cover md:h-96" />
          </div>
          <div>
            <div className="flex items-center gap-3"><HeartHandshake className="h-7 w-7 text-brand" /><span className="font-condensed text-sm font-semibold uppercase tracking-[0.25em] text-brand">Community First</span></div>
            <h2 className="mt-2 display-title text-4xl">Youth Development & Community</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">We believe strong athletes build strong communities. Through youth athletics, Mavs Volleyball, family fitness, and team training, we invest in the next generation — teaching confidence, discipline, and healthy habits that last a lifetime.</p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2"><Volleyball className="h-4 w-4 text-brand" /> Mavs Volleyball youth & club program</li>
              <li className="flex items-center gap-2"><Users className="h-4 w-4 text-brand" /> Family-friendly fitness for all ages</li>
              <li className="flex items-center gap-2"><Trophy className="h-4 w-4 text-brand" /> Camps, clinics & community events</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Media gallery */}
      <section id="about-facilities" className="scroll-mt-24 bg-muted/30 py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Inside Trilogy" title={<SplitWord left="FACILITY " right="& ATHLETES" />} subtitle="Our facilities, coaches, and athletes in action." />
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
            {GALLERY.map((src, i) => (
              <div key={src} className={"overflow-hidden rounded-xl " + (i === 0 ? "col-span-2 md:col-span-1" : "")}>
                <img src={src} alt="Trilogy" loading="lazy" className="h-44 w-full object-cover transition-transform duration-500 hover:scale-105 sm:h-56" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Admin-editable page sections */}
      <PageSections page="about" />

      {/* Coaches (editable from admin, Corey Phillips first) */}
      <section id="about-coaches" className="scroll-mt-24 bg-background py-16 sm:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <SectionTitle eyebrow="Our Team" title={<SplitWord left="THE " right="COACHES" />} subtitle="Meet the coaches leading the Trilogy explosion." />
          <div className="mt-10 space-y-6">
            {loading ? <Loader /> : coaches.map((c, i) => <CoachBlock key={c.id} item={c} reverse={i % 2 === 1} />)}
          </div>
        </div>
      </section>

      <CtaBand title="Train With Purpose" subtitle="Book your free intro workout, explore packages, or download the Trilogy App today." />
    </div>
  );
}


// Coaches list only (no title) — used as the `about_coaches` embed widget so the
// "Our Team" heading/intro can live in an editable section above it, while the
// individual coach cards stay managed in the Coaches content editor.
export const AboutCoaches = () => {
  const { data: coaches, loading } = usePublicContent("coaches");
  return (
    <section className="bg-background py-10 sm:py-14">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
        <div className="space-y-6">
          {loading ? <Loader /> : coaches.map((c, i) => <CoachBlock key={c.id} item={c} reverse={i % 2 === 1} />)}
        </div>
      </div>
    </section>
  );
};
